import React, { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Mail, Phone, MapPin } from "lucide-react";
import { toast, Toaster } from "sonner";
import Layout from "../components/Layout";
import SEO from "../components/SEO";
import { IMAGES } from "../lib/images";
import { api, formatApiErrorDetail } from "../lib/api";

const CATEGORIES = [
  { v: "general", l: "General" },
  { v: "investor", l: "Investor" },
  { v: "media", l: "Media / Press" },
  { v: "development", l: "Development" },
  { v: "partnerships", l: "Partnerships" },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", category: "general", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Message received. We'll be in touch.");
      setForm({ name: "", email: "", phone: "", category: "general", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <SEO title="Contact" description="Contact Elliott International Development — Tampa, Florida. General, investor, media, development, and partnership inquiries." path="/contact" />
      <Toaster theme="dark" position="bottom-right" />
      <section className="relative pt-40 pb-16 px-6 lg:px-12">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="overline">— Contact</div>
            <div className="overline mt-2 text-foreground/50">Tampa, Florida</div>
          </div>
          <div className="lg:col-span-8">
            <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }} className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.98] font-light tracking-tight">
              Let's <em className="text-primary italic">build</em> something extraordinary.
            </motion.h1>
          </div>
        </div>
      </section>

      <section className="px-6 lg:px-12 pb-32">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* CONTACT FORM */}
          <div className="lg:col-span-7 glass p-8 lg:p-12">
            <div className="overline mb-6">— Send a message</div>
            <form onSubmit={submit} data-testid="contact-form" className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <input data-testid="contact-input-name" required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-background border border-input px-5 py-4 focus:border-primary focus:outline-none transition-colors" />
              <input data-testid="contact-input-email" required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-background border border-input px-5 py-4 focus:border-primary focus:outline-none transition-colors" />
              <input data-testid="contact-input-phone" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-background border border-input px-5 py-4 focus:border-primary focus:outline-none transition-colors" />
              <select data-testid="contact-input-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="bg-background border border-input px-5 py-4 focus:border-primary focus:outline-none transition-colors">
                {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
              </select>
              <input data-testid="contact-input-subject" placeholder="Subject (optional)" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="md:col-span-2 bg-background border border-input px-5 py-4 focus:border-primary focus:outline-none transition-colors" />
              <textarea data-testid="contact-input-message" required rows={6} placeholder="Your message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="md:col-span-2 bg-background border border-input px-5 py-4 focus:border-primary focus:outline-none transition-colors resize-none" />
              <button data-testid="contact-submit" disabled={submitting} type="submit" className="md:col-span-2 group inline-flex items-center justify-center gap-3 bg-primary text-primary-foreground px-8 py-5 text-[13px] uppercase tracking-[0.22em] hover:bg-primary/90 transition-all disabled:opacity-60">
                {submitting ? "Sending…" : "Send Message"} <ArrowUpRight className="w-4 h-4" />
              </button>
            </form>
          </div>

          {/* SIDEBAR */}
          <div className="lg:col-span-5 space-y-6">
            <div className="glass p-8">
              <div className="overline mb-6">— Direct lines</div>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <MapPin className="w-5 h-5 text-primary mt-1" />
                  <div>
                    <div className="font-serif text-lg">Tampa Headquarters</div>
                    <div className="font-mono text-xs text-muted-foreground mt-1">410 S Ware Blvd<br />Tampa, FL 33619</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Mail className="w-5 h-5 text-primary mt-1" />
                  <div>
                    <div className="font-serif text-lg">General</div>
                    <div className="font-mono text-xs text-primary mt-1">contact@elliottdev.com</div>
                    <div className="font-serif text-lg mt-3">Investor Relations</div>
                    <div className="font-mono text-xs text-primary mt-1">investors@elliottdev.com</div>
                    <div className="font-serif text-lg mt-3">Press</div>
                    <div className="font-mono text-xs text-primary mt-1">press@elliottdev.com</div>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Phone className="w-5 h-5 text-primary mt-1" />
                  <div>
                    <div className="font-serif text-lg">Phone</div>
                    <div className="font-mono text-xs text-primary mt-1">+1 (703) 489-2129</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative aspect-[4/3] overflow-hidden">
              <img src={IMAGES.aerial_city} alt="Tampa map" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="overline mb-2 text-primary">— Tampa Bay</div>
                <div className="font-serif text-2xl">27.9506° N / 82.4572° W</div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
